// This implementation is based on https://github.com/tasti/react-linkify
import LinkifyIt, { type Match as LinkifyMatch } from 'linkify-it';
import React, { useMemo } from 'react';
import tlds from 'tlds';

const defaultComponentDecorator = (decoratedHref: string, decoratedText: string, key: number) => {
    return (
        <a
            className="underline"
            href={decoratedHref}
            key={key}>
            {decoratedText}
        </a>
    );
};

const defaultHrefDecorator = (href: string) => {
    return href;
};

const linkify = new LinkifyIt();
linkify.tlds(tlds);

const defaultMatchDecorator = (text: string) => {
    return linkify.match(text);
};

const defaultTextDecorator = (text: string) => {
    return text;
};

const useAutoHyperlink = ({
    componentDecorator = defaultComponentDecorator,
    hrefDecorator = defaultHrefDecorator,
    matchDecorator = defaultMatchDecorator,
    textDecorator = defaultTextDecorator
}) => {
    const parseString = (string: string) => {
        if (!string) return string;

        const matches = matchDecorator(string);
        if (!matches) return string;

        const elements = [];
        let lastIndex = 0;

        matches.forEach((match: LinkifyMatch, i: number) => {
            if (match.index > lastIndex) {
                elements.push(string.substring(lastIndex, match.index));
            }

            const decoratedHref = hrefDecorator(match.url);
            const decoratedText = textDecorator(match.text);
            const decoratedComponent = componentDecorator(decoratedHref, decoratedText, i);
            elements.push(decoratedComponent);

            lastIndex = match.lastIndex;
        });

        if (string.length > lastIndex) {
            elements.push(string.substring(lastIndex));
        }

        return elements.length === 1 ? elements[0] : elements;
    };

    const parse = (children: React.ReactNode): React.ReactNode => {
        if (typeof children === 'string') {
            return parseString(children);
        } else if (React.isValidElement(children) && children.type !== 'a' && children.type !== 'button') {
            return React.cloneElement(
                children as React.ReactElement<{ children?: React.ReactNode }>,
                {},
                parse((children as React.ReactElement<{ children?: React.ReactNode }>).props.children)
            );
        } else if (Array.isArray(children)) {
            return children.map((child, i) => <React.Fragment key={i}>{parse(child)}</React.Fragment>);
        }

        return children;
    };

    return parse;
};

type AutoHyperlinkProps = {
    children?: React.ReactNode;
    componentDecorator?: typeof defaultComponentDecorator;
    hrefDecorator?: typeof defaultHrefDecorator;
    matchDecorator?: typeof defaultMatchDecorator;
    textDecorator?: typeof defaultTextDecorator;
};

const AutoHyperlink = ({
    children,
    componentDecorator = defaultComponentDecorator,
    hrefDecorator = defaultHrefDecorator,
    matchDecorator = defaultMatchDecorator,
    textDecorator = defaultTextDecorator
}: AutoHyperlinkProps) => {
    const parse = useAutoHyperlink({ componentDecorator, hrefDecorator, matchDecorator, textDecorator });
    const parsedContent = useMemo(() => parse(children), [children, parse]);

    return <>{parsedContent}</>;
};

export default AutoHyperlink;
